// データマッパー関数 - APIデータの変換
import type { DeviceGenre } from "@/constants/device";
import type {
  DeviceTaskStatus,
  ListSelfReportTargetsResponse,
  ListSelfReportsResponse,
} from "../../_root/packages/-components/DeviceInventoryOperation/query/type";
import type {
  DeviceInventoryDevice,
  DeviceStatus,
  ReportStatus,
} from "../../_root/packages/-components/DeviceInventoryOperation/type";
import { isSelfReportResult, isSelfReportTarget } from "./typeGuards";
import type { ApiDeviceResult } from "./types";
import { validateDeviceCondition } from "./validators";

/**
 * レポートステータスのマッピング
 */
export function mapApiStatusToReportStatus(
  selfReport: ApiDeviceResult["selfReport"] | undefined,
): ReportStatus {
  return selfReport ? "REPORTED" : "NOT_REPORTED";
}

/**
 * デバイスステータスの決定
 */
export function determineDeviceStatus(
  selfReport: ApiDeviceResult["selfReport"] | undefined,
): DeviceStatus {
  if (!selfReport) {
    return "UNREPORTED";
  }

  const condition = validateDeviceCondition(selfReport.deviceCondition);
  return condition || "UNREPORTED";
}

/**
 * APIレスポンスから安全にデバイス情報を変換
 */
export function mapApiDeviceToDevice(
  apiDevice: ApiDeviceResult,
): DeviceInventoryDevice {
  try {
    // 必須フィールドの検証
    if (!apiDevice.device?.id) {
      throw new Error("Device ID is required");
    }

    if (!apiDevice.device.name) {
      throw new Error("Device name is required");
    }

    const device: DeviceInventoryDevice = {
      packageInstanceId: apiDevice.instanceId,
      taskInstanceId: apiDevice.taskInstanceId,
      displayStatus: apiDevice.displayStatus || "REQUEST_BEFORE",
      taskStatus: apiDevice.status,
      deviceId: apiDevice.device.id,
      deviceName: apiDevice.device.name,
      deviceTag: apiDevice.device.deviceTag || undefined,
      deviceImageUrl: apiDevice.device.avatarUrl || undefined,
      deviceGenre: apiDevice.device.genre as DeviceGenre,
      deviceModel: apiDevice.device.model,
      assigneeType: "assigned" as const,
      status: determineDeviceStatus(apiDevice.selfReport),
      reportStatus: mapApiStatusToReportStatus(apiDevice.selfReport),
      selfReportResult: apiDevice.selfReport
        ? {
            reportedAt:
              apiDevice.selfReport.reportedAt ||
              apiDevice.anchorDate ||
              new Date().toISOString(),
            reportedBy: apiDevice.selfReport.reportedBy || "unknown",
            deviceCondition:
              validateDeviceCondition(apiDevice.selfReport.deviceCondition) ||
              "UNREPORTED",
            reportResult: apiDevice.selfReport.reportResult,
            memo: apiDevice.selfReport.memo,
          }
        : undefined,
      assignee: apiDevice.assignedPerson
        ? {
            id: apiDevice.assignedPerson.id,
            name: apiDevice.assignedPerson.name,
            avatarUrl: apiDevice.assignedPerson.avatarUrl || undefined,
          }
        : undefined,
      responsiblePerson: undefined,
      lastUpdatedAt: apiDevice.anchorDate || new Date().toISOString(),
    };

    return device;
  } catch (error) {
    console.error("Failed to map API device to device:", error, apiDevice);
    throw new Error(
      `Invalid device data: ${error instanceof Error ? error.message : "Unknown error"}`,
    );
  }
}

/**
 * セルフレポート対象デバイスをApiDeviceResult形式に変換
 */
export function convertSelfReportTargetToApiDevice(
  target: ListSelfReportTargetsResponse["items"][number],
): ApiDeviceResult | null {
  if (!isSelfReportTarget(target)) {
    return null;
  }

  return {
    instanceId: target.instanceId,
    taskInstanceId: target.taskInstanceId,
    deviceTaskId: target.deviceTaskId,
    device: target.device,
    status: "NotStarted" as DeviceTaskStatus,
    assignedPerson: target.assignedPerson,
    requestId: target.requestId,
    displayStatus: target.displayStatus,
  };
}

/**
 * セルフレポート結果をApiDeviceResult形式に変換
 */
export function convertSelfReportResultToApiDevice(
  result: ListSelfReportsResponse["items"][number],
): ApiDeviceResult | null {
  if (!isSelfReportResult(result)) {
    return null;
  }

  return {
    instanceId: result.instanceId,
    taskInstanceId: result.taskInstanceId,
    deviceTaskId: result.deviceTaskId,
    device: result.device,
    status: result.status,
    selfReport: result.selfReport || undefined,
    assignedPerson: result.assignedPerson,
    anchorDate: result.anchorDate,
    requestId: result.requestId,
    displayStatus: result.displayStatus,
  };
}

/**
 * 複数のAPIデバイスを安全に変換
 */
export function mapApiDevicesToDevices(
  apiDevices: ApiDeviceResult[],
): DeviceInventoryDevice[] {
  const devices: DeviceInventoryDevice[] = [];
  const errors: Error[] = [];

  for (const [index, apiDevice] of apiDevices.entries()) {
    try {
      const device = mapApiDeviceToDevice(apiDevice);
      devices.push(device);
    } catch (error) {
      errors.push(
        new Error(
          `Device at index ${index}: ${error instanceof Error ? error.message : "Unknown error"}`,
        ),
      );
    }
  }

  // エラーがある場合は警告として記録
  if (errors.length > 0) {
    console.warn(`Failed to convert ${errors.length} devices:`, errors);
  }

  return devices;
}
